import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';

const benefits = [
    {
        icon: 'fa-magnifying-glass-chart',
        title: 'Workflow audit',
        description: 'We sit with your team and map the daily, weekly, and monthly tasks that eat the most time — the manual reports, repetitive emails, lookups, approvals, and handoffs.',
    },
    {
        icon: 'fa-robot',
        title: 'AI opportunity mapping',
        description: 'For each workflow, we identify where AI assistants, copilots, or lightweight automation can shave hours — and where it would over-engineer the problem.',
    },
    {
        icon: 'fa-screwdriver-wrench',
        title: 'Tool & stack recommendations',
        description: 'A short list of the AI tools that fit your business — Microsoft Copilot, Claude, ChatGPT Business, Notion AI, custom GPTs, or purpose-built automations — with realistic cost and rollout effort.',
    },
    {
        icon: 'fa-chart-line',
        title: 'Quantified impact estimate',
        description: 'A concrete projection of hours saved per role per month, and where it converts to dollars — measured against your current operating cost.',
    },
    {
        icon: 'fa-graduation-cap',
        title: 'Team enablement preview',
        description: 'A preview of how we would train your team on the chosen tools — including the prompt patterns, governance, and guardrails that turn AI from novelty into a daily habit.',
    },
    {
        icon: 'fa-file-lines',
        title: 'Written report',
        description: 'You walk away with a written assessment you can share with stakeholders — whether you move forward with us or not.',
    },
];

const steps = [
    {
        n: '01',
        title: 'Subscribe to BUILDLAB Manage IT',
        description: 'Activate any Manage IT subscription tier — covering systems, networks, endpoints, identity, and 24/7 monitoring for your business.',
    },
    {
        n: '02',
        title: 'Schedule your AI assessment',
        description: 'Your assessment is included at no additional cost. We book a discovery session within 7 days of activation and meet with your operations leads.',
    },
    {
        n: '03',
        title: 'Receive your roadmap',
        description: 'Within 2 weeks of the discovery session, you receive a written AI Business Integration roadmap with prioritized opportunities, tool recommendations, and a quantified impact estimate.',
    },
    {
        n: '04',
        title: 'Decide your next move',
        description: 'You decide what to act on — handle it in-house with our recommendations, engage BUILDLAB for implementation and training, or simply use the report to inform your strategy.',
    },
];

const ManagedItOfferPage = () => {
    usePageSeo({
        title: 'Managed IT + Free AI Assessment',
        description: 'Subscribe to BUILDLAB Managed IT and get a complimentary AI Business Integration assessment — workflow audit, AI tool recommendations, hours-saved estimate, and team training roadmap.',
        keywords: 'managed IT, AI business integration, AI assessment, Microsoft Copilot, Claude for Work, ChatGPT Business, BUILDLAB offer',
    });

    const ClickHandler = () => {
        window.scrollTo(10, 0);
    };

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle
                    pageTitle={'Manage IT + Free AI Assessment'}
                    pagesub={'Limited-Time Offer '}
                    pageTop={'Offer'}
                />

                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row justify-content-lg-between">
                            <div className="col-lg-5">
                                <div className="heading_block">
                                    <div className="heading_focus_text">
                                        Our
                                        <span className="badge bg-secondary text-white">Offer</span>
                                    </div>
                                    <h2 className="heading_text mb-3">
                                        Subscribe to Manage IT.<br />Get a complimentary AI Business Integration assessment.
                                    </h2>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <p className="mb-3" style={{ fontSize: '1.05rem' }}>
                                    Every business is sitting on hours of work that AI can absorb — drafting, summarizing,
                                    classifying, routing, reporting, looking up answers. The hard part isn't picking a tool.
                                    The hard part is figuring out where to start and what's actually worth automating.
                                </p>
                                <p className="mb-3">
                                    When you subscribe to BUILDLAB <strong>Manage IT</strong> for your business — our
                                    full-service technology operations subscription — we include a complimentary
                                    <strong> AI Business Integration assessment</strong> as part of onboarding. It's the
                                    same engagement we sell separately, included at no additional cost.
                                </p>
                                <p className="mb-0">
                                    You get a working partner for your day-to-day technology <em>and</em> a clear-eyed plan
                                    for how AI can make your operations measurably more efficient — without the guesswork.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="service_section section_space">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                What's
                                <span className="badge bg-secondary text-white">Included</span>
                            </div>
                            <h2 className="heading_text mb-0">
                                Your AI assessment, in detail
                            </h2>
                            <p className="heading_description mb-0">
                                A focused engagement that turns "we should be using AI" into a prioritized list of things to actually do.
                            </p>
                        </div>
                        <div className="row mt-5">
                            {benefits.map((b, i) => (
                                <div className="col-lg-4 col-md-6 mt-30" key={i}>
                                    <div className="iconbox_block layout_icon_left h-100" style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: '12px', padding: '28px 24px' }}>
                                        <div className="iconbox_icon" style={{ background: 'var(--bs-primary-bg-subtle)', width: 52, height: 52, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className={`fa-solid ${b.icon}`} style={{ color: 'var(--bs-primary)', fontSize: '1.25rem' }}></i>
                                        </div>
                                        <div className="iconbox_content">
                                            <h3 className="iconbox_title mb-2" style={{ fontSize: '1.1rem' }}>{b.title}</h3>
                                            <p className="mb-0" style={{ fontSize: '.92rem', lineHeight: 1.6 }}>{b.description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="section_space bg-light">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                How It
                                <span className="badge bg-secondary text-white">Works</span>
                            </div>
                            <h2 className="heading_text mb-0">
                                Four steps from subscribe to roadmap
                            </h2>
                        </div>
                        <div className="row mt-5">
                            {steps.map((s, i) => (
                                <div className="col-lg-3 col-md-6 mt-30" key={i}>
                                    <div className="h-100" style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: '12px', padding: '28px 24px', position: 'relative' }}>
                                        <div style={{ fontFamily: 'Axiforma Bold, sans-serif', fontSize: '2.25rem', color: 'var(--bs-primary)', lineHeight: 1, marginBottom: 12 }}>{s.n}</div>
                                        <h3 className="mb-2" style={{ fontSize: '1.05rem', color: 'var(--bs-dark)' }}>{s.title}</h3>
                                        <p className="mb-0" style={{ fontSize: '.9rem', lineHeight: 1.6 }}>{s.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="section_space">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6">
                                <div className="heading_block">
                                    <div className="heading_focus_text">
                                        About
                                        <span className="badge bg-secondary text-white">Manage IT</span>
                                    </div>
                                    <h2 className="heading_text mb-3">
                                        What's actually in the subscription
                                    </h2>
                                </div>
                                <p>
                                    Manage IT is BUILDLAB's full-service technology operations subscription for growing
                                    businesses. We handle the day-to-day so your team can focus on the work — not on the
                                    tooling underneath it.
                                </p>
                            </div>
                            <div className="col-lg-6">
                                <ul className="list-unstyled" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                    {[
                                        'Systems & network administration',
                                        'Endpoint management & MDM',
                                        'Identity & access management',
                                        'Email & collaboration platforms',
                                        'Backup & disaster recovery',
                                        'Cyber security & monitoring',
                                        'Vendor & license management',
                                        'Help desk for your team',
                                    ].map((item, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '.95rem' }}>
                                            <i className="fa-solid fa-circle-check" aria-hidden="true" style={{ color: 'var(--bs-primary)', marginTop: 4 }}></i>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: '14px', padding: '32px 36px' }}>
                                    <div className="heading_focus_text mb-2">
                                        <span className="badge bg-secondary text-white">Fine Print</span>
                                    </div>
                                    <h3 className="mb-3" style={{ fontSize: '1.3rem', color: 'var(--bs-dark)' }}>The honest details</h3>
                                    <ul className="list-unstyled mb-0" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <li><strong>Who qualifies:</strong> Any business activating a new BUILDLAB Manage IT subscription. One assessment per business.</li>
                                        <li><strong>What it costs:</strong> $0 additional — the assessment is bundled into your Manage IT onboarding.</li>
                                        <li><strong>What you commit to:</strong> Nothing beyond the Manage IT subscription itself. The roadmap is yours to act on however you choose.</li>
                                        <li><strong>Timeline:</strong> Discovery session within 7 days of subscription activation; written roadmap within 2 weeks of discovery.</li>
                                        <li><strong>Offer window:</strong> Available for a limited time as we expand our AI Business Integration practice.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section_space text-center">
                    <div className="container">
                        <h2 className="mb-3" style={{ color: 'var(--bs-dark)' }}>Ready to start?</h2>
                        <p className="mb-4" style={{ fontSize: '1.05rem' }}>
                            Set up a 30-minute call. We'll walk you through Manage IT pricing for your business size and
                            schedule your complimentary AI assessment as part of onboarding.
                        </p>
                        <Link onClick={ClickHandler} to="/contact" className="btn btn-primary">
                            <span className="btn_label" data-text="Talk to BUILDLAB">Talk to BUILDLAB</span>
                            <span className="btn_icon">
                                <i className="fa-solid fa-arrow-up-right" aria-hidden="true"></i>
                            </span>
                        </Link>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ManagedItOfferPage;
